var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Form, useFormioContext, usePagination, } from '@formio/react';
import { useCallback, useState, useRef, useEffect } from 'react';
const getActionInfo = (formId, actionName, token) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield fetch(`/form/${formId}/actions/${actionName}`, {
        headers: { 'x-jwt-token': token },
    });
    if (!response.ok)
        throw new Error(yield response.text());
    return response.json();
});
const getPossibleActionsForm = (id) => ({
    display: 'form',
    components: [
        {
            label: 'Available Actions',
            widget: 'choicesjs',
            hideLabel: true,
            tableView: true,
            dataSrc: 'url',
            data: {
                url: `/form/${id}/actions`,
                headers: [
                    {
                        key: '',
                        value: '',
                    },
                ],
            },
            template: '<span>{{ item.title }}</span>',
            key: 'action',
            type: 'select',
            input: true,
            disableLimit: false,
            noRefreshOnScroll: false,
        },
    ],
});
const isButtonComponent = (comp) => comp.type === 'button';
export const FormActions = ({ formId, limit }) => {
    const [displayedAction, setDisplayedAction] = useState(null);
    const [activeAction, setActiveAction] = useState(null);
    const [currentForm, setCurrentForm] = useState();
    const { Formio, token } = useFormioContext();
    const actionToAdd = useRef(null);
    const fetchFunction = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const formio = new Formio(`/form/${formId}`);
            return formio.loadActions({}, { ignoreCache: true });
        }
        catch (err) {
            console.error('Failed to load form actions:', err);
            return [];
        }
    }), [Formio, formId]);
    const { data, setPage } = usePagination(1, limit || 10, fetchFunction);
    useEffect(() => {
        const formio = new Formio(`/form/${formId}`);
        formio.loadForm().then(setCurrentForm);
    }, [Formio, formId]);
    const handleActionClick = (id) => __awaiter(void 0, void 0, void 0, function* () {
        const formio = new Formio(`/form/${formId}/action/${id}`);
        try {
            const action = yield formio.loadAction(id);
            setDisplayedAction(action);
        }
        catch (err) {
            console.error('Failed to load action:', err);
        }
    });
    const handleAddAction = (action) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const actionInfo = yield getActionInfo(formId, action.name, token);
            setActiveAction({
                form: actionInfo.settingsForm,
                title: actionInfo.title,
                submission: {
                    data: action,
                },
                action: 'Add',
            });
        }
        catch (err) {
            console.error('Failed to add action:', err);
        }
    });
    const handleEditAction = (id, name) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const thisActionFormio = new Formio(`/form/${formId}/action/${id}`);
            const actionPromise = thisActionFormio.loadAction();
            const actionInfoPromise = getActionInfo(formId, name, token);
            const [action, actionInfo] = yield Promise.all([actionPromise, actionInfoPromise]);
            // because the `action` param only allows for POST requests, we need to modify the `settingsForm` to emit a custom action on submit
            const settingsForm = actionInfo.settingsForm;
            const submitButton = settingsForm.components.find((comp) => isButtonComponent(comp) && comp.action === 'submit');
            if (!submitButton) {
                console.warn("Can't edit action without submit button");
                return;
            }
            submitButton.action = 'event';
            submitButton.event = 'updateAction';
            setActiveAction({
                form: settingsForm,
                title: action.title,
                submission: { data: action },
                action: 'Update',
            });
        }
        catch (err) {
            console.error('Failed to load action:', err);
        }
    });
    const handleDeleteAction = (id) => __awaiter(void 0, void 0, void 0, function* () {
        const formio = new Formio(`/form/${formId}/action/${id}`);
        try {
            yield formio.deleteAction();
        }
        catch (err) {
            console.error('Failed to delete action:', err);
        }
        setPage(1);
    });
    const handleCustomEvent = (_a) => __awaiter(void 0, [_a], void 0, function* ({ type, data }) {
        if (type === 'updateAction') {
            const formio = new Formio(`/form/${formId}/action/${data._id}`);
            try {
                yield formio.saveAction(data);
                setPage(1);
                setActiveAction(null);
            }
            catch (err) {
                console.error('Failed to update action:', err);
            }
        }
    });
    const actionsToMap = displayedAction ? [displayedAction] : data;
    return (_jsx("div", { className: "panel form-actions active", children: _jsxs("div", { className: "form-actions-wrap remember-focus-actions", children: [_jsxs("div", { className: "form-action-row heading", children: [_jsx("div", { className: "form-action-title", children: "Name" }), _jsx("div", { className: "form-action-title", children: "Operations" })] }), actionsToMap.map((action) => (_jsxs("div", { className: "form-action-row action", children: [_jsx("div", { className: "form-action-name", children: _jsx("button", { className: "textlink", onClick: () => handleActionClick(action._id), children: action.title || action.name }) }), _jsx("div", { className: "form-action-op", children: _jsxs("div", { className: "button-wrap", children: [_jsxs("button", { className: "button edit-form-action small withicon", onClick: () => handleEditAction(action._id, action.name), children: [_jsx("i", { className: "ri-edit-box-line" }), "Edit"] }), _jsxs("button", { className: "button delete-form-action red small withicon", onClick: () => handleDeleteAction(action._id), children: [_jsx("i", { className: "ri-delete-bin-line" }), "Delete"] })] }) })] }, action._id))), _jsx("div", { className: "form-action-row add", children: _jsxs("div", { className: "form-action-add-wrap", children: [_jsx(Form, { src: getPossibleActionsForm(formId), onChange: (value, _, modified) => {
                                    if (modified) {
                                        actionToAdd.current = value.data.action;
                                    }
                                } }), _jsxs("button", { className: "button withicon add-action", onClick: () => actionToAdd.current && handleAddAction(actionToAdd.current), children: [_jsx("i", { className: "ri-add-line" }), " Add Action"] }), _jsxs("p", { className: "helptext", children: [_jsx("a", { target: "_blank", rel: "noreferrer", href: "https://help.form.io/userguide/form-building/actions", children: "View documentation" }), ' ', "on actions."] })] }) }), activeAction && (_jsxs("div", { className: "entry-details-wrap active", children: [_jsxs("div", { className: "entry-details-menu-wrap", children: [_jsx("button", { className: "close-button close-entry-details", onClick: () => setActiveAction(null), children: _jsx("i", { className: "ri-close-line" }) }), _jsxs("div", { className: "entry-details-title", children: [activeAction.action, " Action: ", activeAction.title] })] }), _jsx("div", { className: "entry-details-content edit-action active", children: _jsx(Form, { src: activeAction.form, submission: activeAction.submission, onCustomEvent: handleCustomEvent, onSubmitDone: () => {
                                    setPage(1);
                                    setActiveAction(null);
                                }, options: { currentForm } }) })] }))] }) }));
};
